import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
    Text,
    TextInput,
    TouchableOpacity,
    View,
    ScrollView,
    Pressable,
    StyleSheet
} from 'react-native'
import { ErrorMessage, Formik } from 'formik'
import * as yup from 'yup'
import MaskInput, { Masks, formatWithMask, Mask } from 'react-native-mask-input'
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons'

// styles
import { colorPalette, globalStyles } from '../../../static/styles/global'

// i18n
import { en as formSelectEn } from '../../../i18n/FormSelect'
import { en as personalInfoEn } from '../../../i18n/PersonalInformationForms'

// components
import CustomButton from '../../shared/button/Button'
import Select from '../../shared/form/Select'
import Card from '../../shared/card/Card'
import CardSeparator from '../../shared/card/CardSeparator'
import ScreenStatus from '../ScreenStatus'

import {
    customMasks,
    deserializeDOB,
    getUnmaskedValue,
    mapSelectKey,
    serializeDOB,
    setMaskedValue,
    validateDate,
    validateDOBFormat,
    validateDOBYear,
    validateSSN,
    validateZipCode,
    validateserialnumber,
    validate6to9,
    validateserial3to5,
    validatecombination,
    validatelength
} from '../../../utils'
import SelectKeyValue from '../../shared/form/SelectKeyValue'
import { Loading } from '../../shared/navigator/Loading'

// stores
import { usePersonalStore } from '../../../stores/personalInfo'

// types
import {
    PersonalStatuses,
    PersonalStatusesLocked,
    PersonalData,
    updatedpersonalData
} from '../../../types/personalTypes'
import HomeDetailsHeader from '../HomeDetailsHeader'
import PersonalBasicInfoSkeleton from '../../../skeletons/PersonalBasicInfoSkeleton'
import { SafeAreaView } from 'react-native-safe-area-context'
//translation
import { useTranslation } from 'react-i18next'
import SearchKeyValue from '../../shared/form/SearchKeyValue'
import CustomDatePickerModal from '../../shared/calendar/CustomDatePickerModal'

import moment from 'moment'
import { useFocusEffect } from '@react-navigation/native'
import { useInitializeStore } from '../../../stores/initialize'
import Toast from 'react-native-toast-message'


// initial data

type InitialValues = Pick<
    updatedpersonalData,
    | 'firstName'
    | 'middleInitial'
    | 'lastName'
    | 'addressLine1'
    | 'addressLine2'
    | 'city'
    | 'stateValue'
    | 'zip'
    | 'genderValue'
    | 'maritalStatusValue'
    | 'employeeDiscount'
> &
    Record<'dateOfBirth', string>
const initialValues: InitialValues = {
    firstName: '',
    middleInitial: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateValue: '',
    zip: '',
    dateOfBirth: '',
    genderValue: '',
    maritalStatusValue: '',
    employeeDiscount: null,

}

const UpdateBasicInformationForm = ({ navigation, route }: any): React.ReactNode => {
    const { t } = useTranslation()
    const [ssnVisible, setSsnVisible] = useState(false)
    const [status, setStatus] = useState<PersonalStatuses | null>(null)
    const [visibledate, setVisibleDate] = useState(false)
    const formikRef = useRef(null)

    const [show, setShow] = useState(false)

    // params
    const { params } = route
    const {
        mostRecentUserOnBoardings: userId,
        states,
        genders,
        maritalStatuses
    } = params
    const [searchText, setSearchText] = useState('')
    const [filteredStates, setFilteredStates] = useState([])

    // stores' data
    const { loading, error, personalData, getUpdatedData, updatedData, updatepersonalinformation, getDisablekeys, disablekeysData } =
        usePersonalStore()
    const intializestore = useInitializeStore()

    useFocusEffect(
        useCallback(() => {
            getUpdatedData(intializestore.initializeData?.userkey);
            getDisablekeys(personalData?.entityId)
        }, [])
    );

    useEffect(() => {
        if (formikRef?.current) {
            setMaskedValue('zip', customMasks.USA_ZIPCODE, formikRef)
            setMaskedValue(
                'dateOfBirth',
                Masks.DATE_MMDDYYYY,
                formikRef,
                deserializeDOB
            )
        }
        return () => { }
    }, [formikRef?.current, loading])


    const handletext = (text: any, setFieldValue: any) => {
        if (text.length > 0) {
            const index = states.findIndex((option: any) => option.stateDescription.toLowerCase() === (text.toLowerCase()))
            if (index != -1) {
                setFieldValue("stateValue", states[index].stateValue)
            }
        }
        else {
            setFieldValue("stateValue", "")
        }
        setDummy(text)
    }


    const [dummy, setDummy] = useState(updatedData?.data?.stateValue || '')
    useEffect(() => {
        if (dummy.length > 0) {
            const filtered = states.filter((option: any) =>
                option.stateDescription.toLowerCase().includes(dummy.toLowerCase())
            )
            setFilteredStates(filtered)
        } else {
            setFilteredStates(states)
        }
    }, [dummy])

    useFocusEffect(
        useCallback(() => {
            const findDescription = () => {
                const value = states.findIndex(
                    (option: any) => option.stateValue == updatedData?.data?.stateValue
                )
                if (value > -1) {
                    setDummy(states[value].stateDescription)
                }
            }
            findDescription()
        }, [updatedData?.data])
    )
    if (loading) return <PersonalBasicInfoSkeleton />

    if (error) return <ScreenStatus message={error} />

    const validatefirst = t('validatefirstName')

    const validatelastName = t('validatelastName')

    const validatessnFormat = t('validatessnFormat')

    const validateaddress = t('validateaddress')

    const validatecity = t('validatecity')

    const validatestate = t('validatestate')

    const validatezipCode = t('validatezipCode')

    const validatezipCodeFormat = t('validatezipCodeFormat')

    const validatedob = t('validatedob')

    const validatedobFormat = t('validatedobFormat')

    const validatedobValideYear = t('validatedobValideYear')

    const validatedobValide = t('validatedobValide')

    const validategender = t('validategender')

    const validatemaritalStatus = t('validatemaritalStatus')

    const validateemployeediscount = t('validateemployeediscount')


    // schema
    const basicInformationSchema = yup.object({
        firstName: yup.string().trim().nullable().required(validatefirst),



        middleInitial: yup.string().trim().length(1, 'Should be 1 character'),

        lastName: yup.string().trim().nullable().required(validatelastName),

        addressLine1: yup

            .string()

            .trim()

            .nullable()

            .required(validateaddress)

            .min(1, 'Should be at least 1 characters')

            .max(24, 'Should be no more than 24 characters'),

        addressLine2: yup

            .string()

            .trim()

            .nullable()

            .max(24, 'Should be no more than 24 characters'),

        city: yup

            .string()

            .trim()

            .nullable()

            .required(validatecity)

            .min(1, 'Should be at least 1 characters')

            .max(22, 'Should be no more than 22 characters'),

        stateValue: yup.string().required(validatestate),

        zip: yup

            .string()

            .nullable()

            .required(validatezipCode)

            .test('is-zipcode', validatezipCodeFormat, (val) => {
                return validateZipCode(val)
            }),

        dateOfBirth: yup

            .string()

            .nullable()

            .required(validatedob)

            .test('is-date-format', validatedobFormat, (val) => {
                return validateDOBFormat(val)
            })

            .test('is-date-valid', validatedobValide, (val) => {
                return validateDate(val)
            })

            .test('is-date-valid-year', validatedobValideYear, (val) => {
                return validateDOBYear(val, 10, 120)
            }),

        genderValue: yup.string().nullable().required(validategender),

        maritalStatusValue: yup.string().nullable().required(validatemaritalStatus),
        employeeDiscount: updatedData?.data?.entityId == 1974 ? yup.string().max(10, 'Maximum length should be 10 digits').required(validateemployeediscount) : yup.string().max(10, 'Maximum length should be 10 digits').nullable()
    })


    return (
        updatedData &&
        (
            <>
                <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
                    <View style={[globalStyles.horizontalPadGap]}>
                        <HomeDetailsHeader
                            title={t('personalinformation')}
                            description={t('personaldescription')}
                        />
                    </View>
                </View>
                <View style={[globalStyles.containerOverlap]}>
                    <Formik
                        enableReinitialize
                        innerRef={formikRef}
                        initialValues={{
                            ...initialValues,
                            ...updatedData.data
                        }}
                        validationSchema={basicInformationSchema}
                        onSubmit={async (values, actions) => {
                            actions.resetForm()

                            // TODO: call action to save values to DB - remove white spaces from values entries on frontend



                            const basicInfoDto = {
                                ...values,
                                zip: getUnmaskedValue(values.zip, customMasks.USA_ZIPCODE),
                                dateOfBirth: serializeDOB(values.dateOfBirth)
                            }

                            delete (basicInfoDto.entityId)
                            delete (basicInfoDto.essUserKey)


                            let finaldto = {
                                entityId: updatedData?.data?.entityId,
                                essUserKey: updatedData?.data?.essUserKey,
                                storeId: personalData.storeEntityId,
                                userPersonalInfo: basicInfoDto
                            }


                            // const data: any = await updatepersonalinformation(finaldto)
                            // if (data.success) {
                            //     navigation.replace('UpdatePersonalContactInformationScreen')
                            // }
                            // else {
                            //     await Toast.show({
                            //         type: 'error',
                            //         position: 'bottom',
                            //         text1: data.message,
                            //         visibilityTime: 3000,
                            //         autoHide: true,
                            //         topOffset: 60
                            //     })
                            // }

                            navigation.navigate('UpdatePersonalContactInformationScreen', { basicInfoDto })
                        }}
                    >
                        {(props) => (
                            <View>
                                <Card>
                                    <View
                                        style={[
                                            globalStyles.hDirection,
                                            globalStyles.vCenterItems,
                                            globalStyles.hBetweenItems
                                        ]}
                                    >
                                        <Text
                                            style={[
                                                globalStyles.text,
                                                globalStyles.fwBold,
                                                globalStyles.fs18,
                                                globalStyles.blackTextColor
                                            ]}
                                        >
                                            {t('basicinformation')}
                                        </Text>
                                        <Text
                                            style={[
                                                globalStyles.text,
                                                globalStyles.fs14,
                                                globalStyles.grayTextColor
                                            ]}
                                        >
                                            <Text style={styles.required}>*</Text>
                                        </Text>
                                    </View>
                                    <CardSeparator light={true} />
                                    <View>
                                        {/* <Text>{JSON.stringify(props, null, 2)}</Text> */}
                                        <View style={[globalStyles.hDirection, globalStyles.mb2]}>
                                            <View style={{ width: '64%', marginRight: '4%' }}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('firstname')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                <TextInput
                                                    style={[
                                                        globalStyles.input,
                                                        globalStyles.inputLight,
                                                        disablekeysData?.firstName === 0 &&
                                                        globalStyles.inputReadOnly,
                                                        props.touched.firstName &&
                                                        props.errors.firstName &&
                                                        globalStyles.inputError, { textTransform: 'uppercase' }
                                                    ]}
                                                    placeholder={''}
                                                    onChangeText={props.handleChange('firstName')}
                                                    value={props.values.firstName || ''}
                                                    onBlur={props.handleBlur('firstName')}
                                                    editable={disablekeysData?.firstName === 1}
                                                />
                                                {props.touched.firstName && props.errors.firstName && (
                                                    <Text style={globalStyles.dangerAlert}>
                                                        {props.errors.firstName as string}
                                                    </Text>
                                                )}
                                            </View>
                                            <View style={{ width: '32%' }}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('middlename')}
                                                </Text>
                                                <TextInput
                                                    style={[
                                                        globalStyles.input,
                                                        globalStyles.inputLight,
                                                        disablekeysData?.middleInitial === 0 &&
                                                        globalStyles.inputReadOnly,
                                                        { textTransform: 'uppercase' }
                                                    ]}
                                                    placeholder={''}
                                                    onChangeText={props.handleChange('middleInitial')}
                                                    value={props.values.middleInitial || ''}
                                                    onBlur={props.handleBlur('middleInitial')}
                                                    maxLength={1}
                                                    editable={disablekeysData?.middleInitial === 1}
                                                />
                                            </View>
                                        </View>
                                        <View style={[globalStyles.mb2]}>
                                            <Text
                                                style={[
                                                    globalStyles.inputLabel,
                                                    globalStyles.inputLabelLarge,
                                                    globalStyles.inputLabelPrimary
                                                ]}
                                            >
                                                {t('lastname')}<Text style={styles.required}>*</Text>
                                            </Text>
                                            <TextInput
                                                style={[
                                                    globalStyles.input,
                                                    globalStyles.inputLight,
                                                    disablekeysData?.lastName === 0 &&
                                                    globalStyles.inputReadOnly,
                                                    props.touched.lastName &&
                                                    props.errors.lastName &&
                                                    globalStyles.inputError,
                                                    { textTransform: 'uppercase' }
                                                ]}
                                                onChangeText={props.handleChange('lastName')}
                                                value={props.values.lastName || ''}
                                                onBlur={props.handleBlur('lastName')}
                                                editable={disablekeysData?.lastName === 1}
                                            />
                                            {props.touched.lastName && props.errors.lastName && (
                                                <Text style={globalStyles.dangerAlert}>
                                                    {props.errors.lastName as string}
                                                </Text>
                                            )}
                                        </View>



                                        <View style={[globalStyles.mb2]}>
                                            <Text
                                                style={[
                                                    globalStyles.inputLabel,
                                                    globalStyles.inputLabelLarge,
                                                    globalStyles.inputLabelPrimary
                                                ]}
                                            >
                                                {t('address')}<Text style={styles.required}>*</Text>
                                            </Text>
                                            <TextInput
                                                style={[
                                                    globalStyles.input,
                                                    globalStyles.inputLight,
                                                    disablekeysData?.addressLine1 === 0 &&
                                                    globalStyles.inputReadOnly,
                                                    props.touched.addressLine1 &&
                                                    props.errors.addressLine1 &&
                                                    globalStyles.inputError,
                                                    { textTransform: 'uppercase' }
                                                ]}
                                                placeholder={''}
                                                onChangeText={props.handleChange('addressLine1')}
                                                value={props.values.addressLine1 || ''}
                                                onBlur={props.handleBlur('addressLine1')}
                                                editable={disablekeysData?.addressLine1 === 1}
                                            />
                                            {props.touched.addressLine1 &&
                                                props.errors.addressLine1 && (
                                                    <Text style={globalStyles.dangerAlert}>
                                                        {props.errors.addressLine1 as string}
                                                    </Text>
                                                )}
                                        </View>
                                        <View style={[globalStyles.mb2]}>
                                            <Text
                                                style={[
                                                    globalStyles.inputLabel,
                                                    globalStyles.inputLabelLarge,
                                                    globalStyles.inputLabelPrimary
                                                ]}
                                            >
                                                {t('Address 2')}
                                            </Text>
                                            <TextInput
                                                style={[
                                                    globalStyles.input,
                                                    globalStyles.inputLight,
                                                    disablekeysData?.addressLine2 === 0 &&
                                                    globalStyles.inputReadOnly,
                                                    props.touched.addressLine2 &&
                                                    props.errors.addressLine2 &&
                                                    globalStyles.inputError,
                                                    { textTransform: 'uppercase' }
                                                ]}
                                                placeholder={''}
                                                onChangeText={props.handleChange('addressLine2')}
                                                value={props.values.addressLine2 || ''}
                                                onBlur={props.handleBlur('addressLine2')}
                                                maxLength={24}
                                                editable={disablekeysData?.addressLine2 === 1}
                                            />
                                            {props.touched.addressLine2 &&
                                                props.errors.addressLine2 && (
                                                    <Text style={globalStyles.dangerAlert}>
                                                        {props.errors.addressLine2 as string}
                                                    </Text>
                                                )}
                                        </View>

                                        <View style={[globalStyles.mb2]}>
                                            <Text
                                                style={[
                                                    globalStyles.inputLabel,
                                                    globalStyles.inputLabelLarge,
                                                    globalStyles.inputLabelPrimary
                                                ]}
                                            >
                                                {t('city')}<Text style={styles.required}>*</Text>
                                            </Text>
                                            <TextInput
                                                style={[
                                                    globalStyles.input,
                                                    globalStyles.inputLight,
                                                    // PersonalStatusesLocked.includes(status) &&
                                                    // globalStyles.inputReadOnly,
                                                    disablekeysData?.city === 0 &&
                                                    globalStyles.inputReadOnly,
                                                    props.touched.city &&
                                                    props.errors.city &&
                                                    globalStyles.inputError,
                                                    { textTransform: 'uppercase' }
                                                ]}
                                                placeholder={''}
                                                onChangeText={props.handleChange('city')}
                                                value={props.values.city || ''}
                                                onBlur={props.handleBlur('city')}
                                                editable={disablekeysData?.city === 1}
                                            />
                                            {props.touched.city && props.errors.city && (
                                                <Text style={globalStyles.dangerAlert}>
                                                    {props.errors.city as string}
                                                </Text>
                                            )}
                                        </View>
                                        <View style={[globalStyles.hDirection, globalStyles.mb2]}>
                                            <View style={[{ width: '48%', marginRight: '2%' }]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('state')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                {/* <Pressable disabled={PersonalStatusesLocked.includes(status)} onPress={() => setShow(!show)}> */}
                                                <Pressable onPress={() => setShow(!show)}>
                                                    <TextInput
                                                        style={[
                                                            globalStyles.input,
                                                            globalStyles.inputLight,
                                                            disablekeysData?.stateValue === 0 &&
                                                            globalStyles.inputReadOnly,
                                                            props.touched.stateValue &&
                                                            props.errors.stateValue &&
                                                            globalStyles.inputError,
                                                            { position: 'relative' },
                                                            { textTransform: 'uppercase' }
                                                        ]}
                                                        onChangeText={(text) => handletext(text, props.setFieldValue)}
                                                        value={dummy}
                                                        editable={disablekeysData?.stateValue === 1}
                                                    />
                                                    <View
                                                        style={{
                                                            position: 'absolute',
                                                            right: 6,

                                                            bottom: 11
                                                        }}
                                                    >
                                                        <Ionicons
                                                            name="chevron-down"
                                                            size={23}
                                                            color={colorPalette.secondary}
                                                        />
                                                    </View>
                                                </Pressable>

                                                {show && disablekeysData?.stateValue === 1 && (
                                                    <ScrollView
                                                        persistentScrollbar={true}
                                                        style={{
                                                            backgroundColor: "#f0f8ff",
                                                            borderRadius: 8,
                                                            paddingVertical: 18,
                                                            paddingHorizontal: 20,
                                                            maxHeight: 150,

                                                            // position: 'absolute',
                                                            // top: 50,
                                                            left: 0,
                                                            right: 0,
                                                            zIndex: 9999
                                                        }}
                                                    >
                                                        {filteredStates?.map((item, index) => (
                                                            <TouchableOpacity
                                                                style={{
                                                                    paddingVertical: 8,
                                                                    paddingHorizontal: 16,
                                                                    borderBottomWidth: 1,
                                                                    borderBottomColor: colorPalette.lightDark,
                                                                    flexDirection: 'row',
                                                                    justifyContent: 'flex-start',
                                                                    alignItems: 'center',
                                                                }}
                                                                key={index}
                                                                onPress={() => {
                                                                    props.setFieldValue(
                                                                        'stateValue',
                                                                        item.stateValue
                                                                    )
                                                                    setDummy(item.stateDescription)
                                                                    setShow(false)
                                                                }}
                                                            >
                                                                <Text>{item.stateDescription}</Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </ScrollView>
                                                )}
                                                {props.touched.stateValue &&
                                                    props.errors.stateValue && (
                                                        <Text style={globalStyles.dangerAlert}>
                                                            {props.errors.stateValue as string}
                                                        </Text>
                                                    )}
                                            </View>
                                            <View style={[{ width: '48%', marginLeft: '2%' }]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('zipcode')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                <MaskInput
                                                    style={[
                                                        globalStyles.input,
                                                        globalStyles.inputLight,
                                                        disablekeysData?.zip === 0 &&
                                                        globalStyles.inputReadOnly,
                                                        props.touched.zip &&
                                                        props.errors.zip &&
                                                        globalStyles.inputError
                                                    ]}
                                                    value={props.values.zip || ''}
                                                    onChangeText={props.handleChange('zip')}
                                                    onBlur={props.handleBlur('zip')}
                                                    mask={customMasks.USA_ZIPCODE}
                                                    keyboardType="numeric"
                                                    editable={disablekeysData?.zip === 1}
                                                />
                                                {props.touched.zip && props.errors.zip && (
                                                    <Text style={globalStyles.dangerAlert}>
                                                        {props.errors.zip as string}
                                                    </Text>
                                                )}
                                            </View>
                                        </View>
                                        <View >
                                            <View style={[globalStyles.mb2]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary,

                                                    ]}
                                                >
                                                    {t('dateofbirth')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                <View style={[globalStyles.inputWithIconWrapper]}>
                                                    <MaskInput
                                                        style={[
                                                            globalStyles.input,
                                                            globalStyles.inputLight,
                                                            disablekeysData?.dateOfBirth === 0 &&
                                                            globalStyles.inputReadOnly,
                                                            props.touched.dateOfBirth &&
                                                            props.errors.dateOfBirth &&
                                                            globalStyles.inputError,
                                                            { paddingRight: 40 }
                                                        ]}
                                                        value={props.values.dateOfBirth || ''}
                                                        // onChangeText={props.handleChange('dateOfBirth')}
                                                        onChangeText={(text) => {
                                                            props.handleChange('dateOfBirth')(text);
                                                        }}
                                                        onBlur={props.handleBlur('dateOfBirth')}
                                                        mask={Masks.DATE_MMDDYYYY}
                                                        keyboardType="numeric"
                                                        placeholder="mm/dd/yyyy"
                                                        editable={disablekeysData?.dateOfBirth === 1}
                                                    />
                                                    <TouchableOpacity
                                                        style={[
                                                            globalStyles.inputWithIcon,
                                                            globalStyles.inputWithIconEnd,
                                                            { bottom: 11 }
                                                        ]}
                                                        onPress={() => {
                                                            if (disablekeysData?.dateOfBirth === 1) {
                                                                setVisibleDate(true)
                                                            }
                                                        }}
                                                        disabled={disablekeysData?.dateOfBirth !== 1}
                                                    >
                                                        <Ionicons
                                                            name="calendar-outline"
                                                            size={22}
                                                            color={disablekeysData?.dateOfBirth === 1 ? colorPalette.secondary : colorPalette.lightDark}
                                                        />
                                                    </TouchableOpacity>
                                                </View>

                                                <CustomDatePickerModal
                                                    visible={visibledate && disablekeysData?.dateOfBirth === 1}
                                                    value={
                                                        props.values.dateOfBirth &&
                                                            moment(props.values.dateOfBirth, 'MM/DD/YYYY', true).isValid()
                                                            ? moment(props.values.dateOfBirth, 'MM/DD/YYYY').toDate()
                                                            : new Date('2000-01-01')
                                                    }
                                                    minimumDate={new Date(1920, 0, 1)}
                                                    maximumDate={new Date()}
                                                    onConfirm={(date) => {
                                                        const selectedDate = moment(date).format('MM/DD/YYYY')
                                                        setVisibleDate(false)
                                                        props.setFieldValue('dateOfBirth', selectedDate)
                                                    }}
                                                    onCancel={() => setVisibleDate(false)}
                                                />
                                                {props.touched.dateOfBirth &&
                                                    props.errors.dateOfBirth && (
                                                        <Text style={globalStyles.dangerAlert}>
                                                            {props.errors.dateOfBirth as string}
                                                        </Text>
                                                    )}
                                            </View>
                                        </View>

                                        <View style={[globalStyles.hDirection, globalStyles.mb2]}>
                                            <View
                                                style={[
                                                    { width: '48%', marginRight: '2%' }
                                                ]}
                                            >
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('gender')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                <SelectKeyValue
                                                    title={'Gender'}
                                                    options={genders}
                                                    selectedOption={mapSelectKey(
                                                        genders,
                                                        updatedData?.data?.genderValue
                                                    )}
                                                    onSelect={(option: string) => {
                                                        props.setFieldValue('genderValue', option)
                                                    }}
                                                    status={{
                                                        touched: props.touched.genderValue,
                                                        error: props.errors.genderValue
                                                    }}
                                                    isDisabled={disablekeysData?.genderValue === 0}
                                                />

                                                {props.touched.genderValue &&
                                                    props.errors.genderValue && (
                                                        <Text style={globalStyles.dangerAlert}>
                                                            {props.errors.genderValue as string}
                                                        </Text>
                                                    )}
                                            </View>
                                            <View
                                                style={[{ width: '48%', marginLeft: '2%' }]}
                                            >
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('maritalstatus')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                <SelectKeyValue
                                                    title={'Marital Status'}
                                                    options={maritalStatuses}
                                                    selectedOption={mapSelectKey(
                                                        maritalStatuses,
                                                        updatedData?.data?.maritalStatusValue
                                                    )}
                                                    onSelect={(option: string) => {
                                                        props.setFieldValue('maritalStatusValue', option)
                                                    }}
                                                    status={{
                                                        touched: props.touched.maritalStatusValue,
                                                        error: props.errors.maritalStatusValue
                                                    }}
                                                    isDisabled={disablekeysData?.maritalStatusValue === 0}
                                                />
                                                {props.touched.maritalStatusValue &&
                                                    props.errors.maritalStatusValue && (
                                                        <Text style={globalStyles.dangerAlert}>
                                                            {props.errors.maritalStatusValue as string}
                                                        </Text>
                                                    )}
                                            </View>

                                        </View>
                                        {props.values.employeeDiscount &&
                                            <View style={[globalStyles.mb2]}>
                                                <Text
                                                    style={[
                                                        globalStyles.inputLabel,
                                                        globalStyles.inputLabelLarge,
                                                        globalStyles.inputLabelPrimary
                                                    ]}
                                                >
                                                    {t('Employee Discount')}<Text style={styles.required}>*</Text>
                                                </Text>
                                                <TextInput
                                                    style={[
                                                        globalStyles.input,
                                                        globalStyles.inputLight,
                                                        disablekeysData?.employeeDiscount === 0 &&
                                                        globalStyles.inputReadOnly,
                                                        props.touched.employeeDiscount &&
                                                        props.errors.employeeDiscount &&
                                                        globalStyles.inputError
                                                    ]}
                                                    placeholder={''}
                                                    onChangeText={(text) => {
                                                        if (text === '' || /^\d+$/.test(text)) {
                                                            props.handleChange('employeeDiscount')(text)
                                                        }
                                                    }}
                                                    value={props.values.employeeDiscount || ''}
                                                    onBlur={props.handleBlur('employeeDiscount')}
                                                    editable={disablekeysData?.employeeDiscount === 1}
                                                    keyboardType='numeric'
                                                />
                                                {props.touched.employeeDiscount &&
                                                    props.errors.employeeDiscount && (
                                                        <Text style={globalStyles.dangerAlert}>
                                                            {props.errors.employeeDiscount as string}
                                                        </Text>
                                                    )}
                                            </View>
                                        }
                                    </View>
                                </Card>
                                <View style={[globalStyles.mt2, globalStyles.mb5]}>
                                    <CustomButton text={t('next')} onPress={props.handleSubmit} />
                                </View>
                            </View>
                        )}
                    </Formik >

                </View >
            </>
        )
    )
}


const styles = StyleSheet.create({
    required: {
        color: "red"
    }
})

export default UpdateBasicInformationForm

